import React, { useState, useCallback, useRef, memo } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Container,
  Alert,
  CircularProgress,
  Card
} from '@mui/material';
import {
  Login as LoginIcon,
  SportsBasketball as BasketballIcon,
  Search as SearchIcon,
  Analytics as AnalyticsIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
interface HomeScreenProps {
  onLoginSuccess: () => void;
}

const ZoneAILogo: React.FC<{ size?: number; color?: string }> = ({ size = 200, color = '#3D263B' }) => (
  <img 
    src="/zoneai-logo.svg" 
    alt="ZoneAI Logo" 
    style={{ 
      width: size, 
      height: size * 0.25,
      filter: color === '#ffffff' ? 'brightness(0) invert(1)' : 'none'
    }} 
  />
);

export const HomeScreen: React.FC<HomeScreenProps> = ({ onLoginSuccess }) => {
  const [error, setError] = useState('');
  const { login, isLoading } = useAuth();
  
  // Use refs for uncontrolled components to prevent focus loss
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  // Handle submit with refs instead of state
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    const email = emailRef.current?.value || '';
    const password = passwordRef.current?.value || '';
    
    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }

    const success = await login(email, password);
    if (success) {
      onLoginSuccess();
    } else {
      setError('Invalid email or password');
    }
  }, [login, onLoginSuccess]);

  // Handle key press for return key
  const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit(e as any);
    }
  }, [handleSubmit]);

  // Uncontrolled TextField components - no state, no re-renders
  const EmailField = memo(() => (
    <TextField
      inputRef={emailRef}
      fullWidth
      label="Email Address"
      type="email"
      defaultValue=""
      autoComplete="email"
      onKeyPress={handleKeyPress}
      sx={{
        mb: 2,
        '& .MuiOutlinedInput-root': {
          bgcolor: 'rgba(255, 255, 255, 0.1)',
          borderRadius: 2,
          '& fieldset': {
            borderColor: 'rgba(255, 255, 255, 0.3)',
          },
          '&:hover fieldset': {
            borderColor: 'rgba(255, 255, 255, 0.5)',
          },
          '&.Mui-focused fieldset': {
            borderColor: '#E65E33',
          },
        },
        '& .MuiInputLabel-root': {
          color: 'rgba(255, 255, 255, 0.7)',
        },
        '& .MuiInputBase-input': {
          color: 'white',
        },
      }}
    />
  ));

  const PasswordField = memo(() => (
    <TextField
      inputRef={passwordRef}
      fullWidth
      label="Password"
      type="password"
      defaultValue=""
      autoComplete="current-password"
      onKeyPress={handleKeyPress}
      sx={{
        mb: 3,
        '& .MuiOutlinedInput-root': {
          bgcolor: 'rgba(255, 255, 255, 0.1)',
          borderRadius: 2,
          '& fieldset': {
            borderColor: 'rgba(255, 255, 255, 0.3)',
          },
          '&:hover fieldset': {
            borderColor: 'rgba(255, 255, 255, 0.5)',
          },
          '&.Mui-focused fieldset': {
            borderColor: '#E65E33',
          },
        },
        '& .MuiInputLabel-root': {
          color: 'rgba(255, 255, 255, 0.7)',
        },
        '& .MuiInputBase-input': {
          color: 'white',
        },
      }}
    />
  ));



  // Simple Mobile Login Form - No animations, no complexity
  const MobileLoginForm = () => (
    <Box
      sx={{
        height: '100vh',
        width: '100vw',
        bgcolor: '#3D263B',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        px: 3,
        py: 6,
        boxSizing: 'border-box'
      }}
    >
      {/* Logo */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <ZoneAILogo size={200} color="#ffffff" />
                <Typography
                  variant="h5"
                  sx={{
                    color: 'white',
                    fontWeight: 600,
                    mt: 2,
                    textAlign: 'center',
                    fontSize: '1.2rem'
                  }}
                >
                  Navigate the Transfer Portal
                </Typography>
      </Box>

      {/* Login Form */}
      <Box
        sx={{
          width: '100%',
          maxWidth: 400,
          p: 3,
          mt: 16
        }}
      >
        <form onSubmit={handleSubmit}>
          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {error}
            </Alert>
          )}
          
          <EmailField />
          <PasswordField />
          
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={isLoading}
            sx={{
              bgcolor: '#E65E33',
              color: 'white',
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 600,
              borderRadius: 2,
              textTransform: 'none',
              '&:hover': {
                bgcolor: '#D54A1F'
              },
              '&:disabled': {
                bgcolor: 'rgba(230, 94, 51, 0.5)'
              }
            }}
          >
            {isLoading ? 'Signing In...' : 'Sign In'}
          </Button>
        </form>
      </Box>
    </Box>
  );


  return (
    <>
      {/* Mobile Login Form - Simple, no animations */}
      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
        <MobileLoginForm />
      </Box>

      {/* Desktop Layout */}
      <Box sx={{ 
        display: { xs: 'none', md: 'block' },
        minHeight: '100vh', 
        bgcolor: '#FAFAFA',
        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif'
      }}>
      {/* Header */}
      <Box sx={{ 
        bgcolor: '#3D263B',
        py: 4,
        boxShadow: '0 2px 8px rgba(61, 38, 59, 0.15)'
      }}>
        <Container maxWidth="lg">
          <Box display="flex" alignItems="center" justifyContent="center">
            <ZoneAILogo size={300} color="#ffffff" />
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 6 }}>
        {/* Mobile Layout - Sign in first */}
        <Box display={{ xs: 'block', md: 'none' }}>
          {/* Login Form - First thing after header on mobile */}
          <Box display="flex" justifyContent="center" mb={6}>
            <Box sx={{ width: '100%', maxWidth: 400 }}>
              <Paper sx={{ 
                p: 4, 
                borderRadius: 3,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                border: '1px solid rgba(61, 38, 59, 0.08)'
              }}>
                <Box textAlign="center" mb={4}>
                  <BasketballIcon sx={{ 
                    color: '#E65E33', 
                    fontSize: '3rem', 
                    mb: 2 
                  }} />
                  <Typography 
                    variant="h4" 
                    sx={{ 
                      fontWeight: 700,
                      color: '#3D263B',
                      mb: 1
                    }}
                  >
                    Welcome Back
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      color: '#6B7280',
                      fontSize: '1.1rem'
                    }}
                  >
                    Sign in to access the recruiting platform
                  </Typography>
                </Box>

                {error && (
                  <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                    {error}
                  </Alert>
                )}

                <form onSubmit={handleSubmit}>
                  <TextField
                    inputRef={emailRef}
                    fullWidth
                    label="Email Address"
                    type="email"
                    defaultValue=""
                    autoComplete="email"
                    onKeyPress={handleKeyPress}
                    sx={{ mb: 3 }}
                    disabled={isLoading}
                  />
                  
                  <TextField
                    inputRef={passwordRef}
                    fullWidth
                    label="Password"
                    type="password"
                    defaultValue=""
                    autoComplete="current-password"
                    onKeyPress={handleKeyPress}
                    sx={{ mb: 4 }}
                    disabled={isLoading}
                  />

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={isLoading}
                    startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <LoginIcon />}
                    sx={{
                      bgcolor: '#E65E33',
                      color: 'white',
                      py: 1.5,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      borderRadius: 2,
                      textTransform: 'none',
                      '&:hover': {
                        bgcolor: '#D54A1F'
                      }
                    }}
                  >
                    {isLoading ? 'Signing In...' : 'Sign In'}
                  </Button>
                </form>
              </Paper>
            </Box>
          </Box>

          {/* Main Title for mobile */}
          <Box textAlign="center" mb={6}>
            <Typography 
              variant="h2" 
              sx={{ 
                fontWeight: 700,
                color: '#3D263B',
                letterSpacing: '-0.02em',
                mb: 3,
                fontSize: '2.5rem'
              }}
            >
              AI Driven College Basketball Recruiting
            </Typography>
            
            <Typography 
              variant="h5" 
              sx={{ 
                color: '#6B7280',
                mb: 4,
                lineHeight: 1.6,
                fontWeight: 400
              }}
            >
              Find similar players using advanced analytics and AI-powered matching algorithms
            </Typography>
          </Box>

          {/* Feature Cards for mobile */}
          <Box display="flex" flexDirection="column" gap={3} justifyContent="center">
            <Box sx={{ flex: 1, maxWidth: 400, mx: 'auto' }}>
              <Card sx={{ 
                p: 2, 
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                border: '1px solid rgba(61, 38, 59, 0.08)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between'
              }}>
                <Box display="flex" alignItems="center" mb={1}>
                  <SearchIcon sx={{ color: '#E65E33', mr: 1, fontSize: '2rem' }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#3D263B', fontSize: '1.2rem', whiteSpace: 'nowrap' }}>
                    Smart Search
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem', lineHeight: 1.4 }}>
                  Search all Division 1 NCAA college basketball players to find the best matches for your program
                </Typography>
              </Card>
            </Box>

            <Box sx={{ flex: 1, maxWidth: 400, mx: 'auto' }}>
              <Card sx={{ 
                p: 2, 
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                border: '1px solid rgba(61, 38, 59, 0.08)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between'
              }}>
                <Box display="flex" alignItems="center" mb={1}>
                  <AnalyticsIcon sx={{ color: '#E65E33', mr: 1, fontSize: '2rem' }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#3D263B', fontSize: '1.2rem', whiteSpace: 'nowrap' }}>
                    AI Matching
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem', lineHeight: 1.4 }}>
                  Leverage cutting edge technology to give your program a competitive edge in recruiting
                </Typography>
              </Card>
            </Box>
          </Box>
        </Box>

        {/* Desktop Layout - Original side-by-side layout */}
        <Box display={{ xs: 'none', md: 'flex' }} flexDirection="row" gap={4} alignItems="center">
          {/* Left side - Features */}
          <Box sx={{ flex: 1 }}>
            <Box>
              <Typography 
                variant="h2" 
                sx={{ 
                  fontWeight: 700,
                  color: '#3D263B',
                  letterSpacing: '-0.02em',
                  mb: 3,
                  fontSize: '3.5rem'
                }}
              >
                AI Driven College Basketball Recruiting
              </Typography>
              
              <Typography 
                variant="h5" 
                sx={{ 
                  color: '#6B7280',
                  mb: 4,
                  lineHeight: 1.6,
                  fontWeight: 400
                }}
              >
                Find similar players using advanced analytics and AI-powered matching algorithms
              </Typography>

              <Box display="flex" flexDirection="row" gap={3}>
                <Box sx={{ flex: 1 }}>
                  <Card sx={{ 
                    p: 2, 
                    borderRadius: 3,
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                    border: '1px solid rgba(61, 38, 59, 0.08)',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between'
                  }}>
                    <Box display="flex" alignItems="center" mb={1}>
                      <SearchIcon sx={{ color: '#E65E33', mr: 1, fontSize: '2rem' }} />
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#3D263B', fontSize: '1.2rem', whiteSpace: 'nowrap' }}>
                        Smart Search
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem', lineHeight: 1.4 }}>
                      Search all Division 1 NCAA college basketball players to find the best matches for your program
                    </Typography>
                  </Card>
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Card sx={{ 
                    p: 2, 
                    borderRadius: 3,
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                    border: '1px solid rgba(61, 38, 59, 0.08)',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between'
                  }}>
                    <Box display="flex" alignItems="center" mb={1}>
                      <AnalyticsIcon sx={{ color: '#E65E33', mr: 1, fontSize: '2rem' }} />
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#3D263B', fontSize: '1.2rem', whiteSpace: 'nowrap' }}>
                        AI Matching
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem', lineHeight: 1.4 }}>
                      Leverage cutting edge technology to give your program a competitive edge in recruiting
                    </Typography>
                  </Card>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Right side - Login Form */}
          <Box sx={{ flex: 1 }}>
            <Paper sx={{ 
              p: 4, 
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
              border: '1px solid rgba(61, 38, 59, 0.08)'
            }}>
              <Box textAlign="center" mb={4}>
                <BasketballIcon sx={{ 
                  color: '#E65E33', 
                  fontSize: '3rem', 
                  mb: 2 
                }} />
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontWeight: 700,
                    color: '#3D263B',
                    mb: 1
                  }}
                >
                  Welcome Back
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: '#6B7280',
                    fontSize: '1.1rem'
                  }}
                >
                  Sign in to access the recruiting platform
                </Typography>
              </Box>

              {error && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                  {error}
                </Alert>
              )}

              <form onSubmit={handleSubmit}>
                <TextField
                  inputRef={emailRef}
                  fullWidth
                  label="Email Address"
                  type="email"
                  defaultValue=""
                  autoComplete="email"
                  onKeyPress={handleKeyPress}
                  sx={{ mb: 3 }}
                  disabled={isLoading}
                />
                
                <TextField
                  inputRef={passwordRef}
                  fullWidth
                  label="Password"
                  type="password"
                  defaultValue=""
                  autoComplete="current-password"
                  onKeyPress={handleKeyPress}
                  sx={{ mb: 4 }}
                  disabled={isLoading}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={isLoading}
                  startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <LoginIcon />}
                  sx={{
                    bgcolor: '#E65E33',
                    color: 'white',
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    borderRadius: 2,
                    textTransform: 'none',
                    '&:hover': {
                      bgcolor: '#D54A1F'
                    }
                  }}
                >
                  {isLoading ? 'Signing In...' : 'Sign In'}
                </Button>
              </form>
            </Paper>
          </Box>
        </Box>
      </Container>
      </Box>
    </>
  );
};
